/**
 * `/pws-partner/:slug` — PWS Partner Storefront (Feb 2026 rebuild).
 *
 * Full sales-page treatment replacing the earlier "preview" layout.
 * 8 sections: Hero · Meet Your Expert · Why Choose · Services · Local
 * Service Area · Reviews · Before/After (optional) · FAQ · Final CTA.
 *
 * Content contract (all admin-managed in v1):
 *   business_name, region_label, franchisee_name, franchisee_photo_url,
 *   franchisee_logo_url, years_in_business, experience_bio,
 *   why_choose_bio, services_offered (dict), service_suburbs,
 *   testimonials[], gallery_before_after[], faqs[], phone_display,
 *   email_display.
 *
 * Every section hides itself when its content is empty so partners
 * without (say) reviews or before/after photos never see empty
 * placeholder blocks. Every CTA carries `?p=<partner_slug>` so the
 * partner attribution follows the visitor into `/book`.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail, ShieldCheck, Star, Award, Sparkles, ChevronDown, Droplets, Shield, Zap, Waves, Wrench, FlaskConical, CalendarClock, CheckCircle2, FileText, HeartHandshake } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { GoogleReviewsSection } from "@/components/GoogleReviewsSection";
import { SchemaJsonLd, breadcrumbSchema } from "@/lib/seo";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

// SCWT-STOREFRONT-BRAND-HERO · Feb 2026 — Static form-input
// placeholders shown to admins in the Storefront Editor when a
// field is blank. The ACTUAL fallback rendered on the storefront
// is built dynamically inside the hero JSX so it can weave the
// partner's business name + region straight into the copy.
const BRAND_HERO_HEADLINE    = "Professional Water Testing";
const BRAND_HERO_SUBHEADLINE = "By {business name} — your local AWARE Water Testing partner";
const BRAND_LIFESTYLE_IMG    = "https://customer-assets-jt897jd0.emergentagent.net/job_pristine-lab/artifacts/1vas93yn_ChatGPT%20Image%20Aug%208%2C%202026%2C%2011_53_44%20AM.png";

// Global AWARE logo used in the partner header's co-brand slot.
// Transparent header-sized version to sit cleanly next to any
// partner logo/name without a colour clash.
const AWARE_LOGO_URL = "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/zi3hrojv_AWARE_LOGO_header_large-removebg-preview.png";

// Fixed trust markers (per user decision — not admin-editable).
const HERO_TRUST_STRIP = [
  { icon: FlaskConical, title: "Professional",   sub: "laboratory testing" },
  { icon: FileText,     title: "Easy-to-understand", sub: "reporting" },
  { icon: HeartHandshake, title: "Local support", sub: "when you need it" },
];

const BASE_URL = SEO_DEFAULTS.baseUrl;

// Feb 2026 · Service catalogue — must stay in lockstep with backend
// `pws_partner_landing_service.SERVICE_KEYS`. Adding a new service is
// backwards-compatible: legacy partner docs simply see falsy for the
// new key and the tile stays hidden.
const SERVICE_META = {
  water_testing:    { label: "Water Testing",     icon: FlaskConical, blurb: "Independent laboratory testing for tank, bore and town water." },
  tank_cleaning:    { label: "Tank Cleaning",     icon: Droplets,     blurb: "Full drain, scrub and inspection of your rainwater tank." },
  filtration:       { label: "Filtration",        icon: Shield,       blurb: "Whole-house and point-of-use filtration matched to your water report." },
  biocleanse:       { label: "BioCleanse",        icon: Waves,        blurb: "Deep-clean sanitisation for tanks and lines with heavy biological load." },
  uv_systems:       { label: "UV Systems",        icon: Zap,          blurb: "Ultraviolet sterilisation for microbiological safety at the tap." },
  annual_servicing: { label: "Annual Servicing",  icon: CalendarClock, blurb: "Recurring maintenance so your water quality stays consistent year on year." },
};

function bookHref(partnerSlug, extra = {}) {
  const q = new URLSearchParams({ p: partnerSlug, ...extra }).toString();
  return `/book?${q}`;
}

/**
 * PartnerHeader — SCWT-STOREFRONT-PARTNER-HEADER · Feb 2026.
 *
 * Sticky, partner-branded header used ONLY on the storefront route.
 * Replaces the AWARE global site chrome so local conversion pages
 * feel like a bespoke small-business landing page rather than a
 * franchise sub-page.
 *
 * Left:
 *   • franchisee_logo_url (image, 36px tall) OR the business name
 *     as fallback text.
 *   • Small "Local PWS Partner · Powered by AWARE Water Testing"
 *     co-branding line so the master brand is present but not loud.
 * Right:
 *   • Phone (tel: link, hidden on mobile widths where the CTA is
 *     more valuable real-estate).
 *   • Email (mailto: link, hidden on smaller widths for the same
 *     reason).
 *   • "Book Water Test" primary CTA (always visible).
 */
function PartnerHeader({ data, businessName, heroCta }) {
  const phone      = (data?.phone_display || "").trim();
  const email      = (data?.email_display || "").trim();
  // SCWT-STOREFRONT-PARTNER-BRANDING · Feb 2026 — Prefer the
  // Partner Branding logo (`partners.branding.logo_*`) that admins
  // already maintain for white-label PDFs. Fall back to the
  // landing-page-specific `franchisee_logo_url` if the branding
  // logo hasn't been uploaded, then to the business-name text.
  const logo       = (
    data?.partner_brand_logo_url
    || data?.franchisee_logo_url
    || ""
  ).trim();
  const phoneHref  = phone ? `tel:${phone.replace(/\s/g, "")}` : null;
  return (
    <header
      data-testid="storefront-partner-header"
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        {/* ── Left: partner brand + AWARE co-brand ─────────── */}
        <div className="flex items-center gap-3 min-w-0">
          {logo ? (
            <img
              src={logo}
              alt={businessName}
              className="h-9 w-auto max-w-[180px] object-contain shrink-0"
              data-testid="storefront-partner-header-logo"
            />
          ) : (
            <div
              className="font-bold text-[#0d2451] text-base sm:text-lg truncate max-w-[200px]"
              data-testid="storefront-partner-header-name"
            >
              {businessName}
            </div>
          )}
          {/* Vertical divider + AWARE master-brand logo. Hidden on
              the smallest screens so the partner brand keeps
              breathing room. */}
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            <div className="w-px h-8 bg-slate-200" aria-hidden="true" />
            <img
              src={AWARE_LOGO_URL}
              alt="AWARE Water Testing"
              className="h-8 w-auto object-contain"
              data-testid="storefront-partner-header-aware-logo"
            />
          </div>
        </div>

        {/* ── Right: contact + Book CTA ────────────────────── */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {phone && (
            <a
              href={phoneHref}
              data-testid="storefront-partner-header-phone"
              className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-[#0d2451] hover:text-[#106c81]"
            >
              <Phone className="w-4 h-4" /> {phone}
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              data-testid="storefront-partner-header-email"
              className="hidden lg:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#106c81] max-w-[220px] truncate"
            >
              <Mail className="w-4 h-4 shrink-0" /> <span className="truncate">{email}</span>
            </a>
          )}
          <Link to={heroCta}>
            <Button
              size="sm"
              data-testid="storefront-partner-header-book-cta"
              className="rounded-md bg-[#106c81] hover:bg-[#0a5a6b] text-white h-9 px-4 font-semibold shadow-sm"
            >
              <CalendarClock className="w-4 h-4 mr-1.5" /> Book Water Test
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function PwsPartnerStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/pws-partner-landing/by-slug/${slug}`);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Jun 2026 — per-partner manifest swap. The site-wide manifest has
  // start_url:"/" so iOS "Add to Home Screen" launched the HOMEPAGE
  // instead of the saved partner page. Point the manifest link at the
  // partner-specific manifest (start_url = this storefront, named
  // after the partner) while this page is mounted; restore on leave.
  useEffect(() => {
    if (!slug) return undefined;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return undefined;
    const original = link.getAttribute("href");
    link.setAttribute("href", `/api/pws-partner-landing/by-slug/${encodeURIComponent(slug)}/manifest.json`);
    // Fallback for iOS launches that ignore the swapped manifest and
    // open the site-wide start_url ("/"): remember which partner page
    // was being viewed so App.js can forward standalone launches here.
    try { localStorage.setItem("aware_pwa_target", `/pws-partner/${slug}`); } catch { /* ignore */ }
    return () => { if (original) link.setAttribute("href", original); };
  }, [slug]);

  // Jun 2026 — landing analytics: fire qr_scan when the page was
  // opened from a QR poster (?qr=1 via the /p/ short link), otherwise
  // landing_page_view. Fire-and-forget, once per mount, stable
  // per-session visitor id.
  useEffect(() => {
    if (!slug) return;
    let vid = null;
    try {
      vid = sessionStorage.getItem("aware_lp_vid");
      if (!vid) {
        vid = `v-${Math.random().toString(36).slice(2, 14)}`;
        sessionStorage.setItem("aware_lp_vid", vid);
      }
      // Once per session per page — guards against StrictMode double
      // mounts and the /p/ short-link redirect remount.
      if (sessionStorage.getItem(`aware_lp_evt_${slug}`)) return;
      sessionStorage.setItem(`aware_lp_evt_${slug}`, "1");
    } catch { /* private mode — send without visitor id */ }
    const isQr = new URLSearchParams(window.location.search).has("qr");
    api.post(`/pws-partner-landing/by-slug/${slug}/events`, {
      event: isQr ? "qr_scan" : "landing_page_view",
      visitor_id: vid,
    }).catch(() => {});
  }, [slug]);

  const businessName = data?.business_name || "PWS Partner";
  const region       = data?.region_label  || "";
  // Jun 2026 GSC audit — the old template ("<name> — Water Testing in
  // <region>") blew past 60 chars and got cut to "… — Water Testing in".
  // The region already leads the H1 and the description.
  const seoTitle       = data ? `${businessName} — Water Testing` : "PWS Partner — Water Testing";
  const seoDescription = data
    ? `${businessName} is your local PWS Partner${region ? ` for ${region}` : ""}. Book online for tank, bore and rainwater testing plus tank cleaning, filtration and BioCleanse services.`
    : "Book your local PWS Partner for professional water testing and treatment services.";

  // Jun 2026 GSC audit ("Duplicate — Google chose different canonical"):
  // the canonical used to echo the URL as typed, so /pws-partner/PWS-South-
  // Gippsland self-canonicalised to the mixed-case variant and became its
  // own indexable duplicate. Always canonicalise to the partner's real
  // lower-case slug.
  const canonicalSlug = (data?.slug || slug || "").toLowerCase();

  useSEO({
    title: seoTitle,
    description: seoDescription,
    url: `${BASE_URL}/pws-partner/${canonicalSlug}`,
    // noindex once we KNOW the page is unpublished or missing — the
    // loading state must not emit noindex or Google's renderer may
    // snapshot it (same trap as BlogPost). The "Partner not found"
    // branch is a thin soft-404 and must never be indexable.
    noindex: loading ? false : (!data || data.status !== "published"),
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center text-slate-500 text-sm">Loading partner…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        {/* Blank placeholder header — the partner-branded one needs
            partner data which we don't have on this 404 branch. */}
        <header className="h-14 border-b border-slate-200" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Partner not found</h1>
          <p className="mt-3 text-slate-600">This partner page isn&#39;t published yet.</p>
          <Link to="/partner-network" className="mt-6 inline-flex items-center gap-1 text-cyan-700 font-semibold hover:text-cyan-900">
            Find your local Partner <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const partnerSlug = data.partner_slug || slug;
  const services = SERVICE_KEYS_ORDERED
    .filter((k) => data.services_offered?.[k])
    .map((k) => ({ key: k, ...SERVICE_META[k] }));
  const suburbs      = data.service_suburbs || [];
  const testimonials = data.testimonials    || [];
  const gallery      = data.gallery_before_after || [];
  const faqs         = data.faqs || [];
  const hasReviews   = testimonials.length > 0;
  const hasGallery   = gallery.length > 0;
  const hasFaqs      = faqs.length > 0;

  const heroCta = bookHref(partnerSlug);

  const breadcrumbs = [
    { name: "Home",       url: BASE_URL },
    { name: "Partners",   url: `${BASE_URL}/partner-network` },
    { name: businessName, url: `${BASE_URL}/pws-partner/${canonicalSlug}` },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`pws-partner-storefront-${slug}`}>
      {/* SCWT-STOREFRONT-PARTNER-HEADER · Feb 2026 — Partner-branded
          sticky header replaces the AWARE global chrome on storefront
          routes. Left: partner logo (or business name) + small
          co-branding line. Right: click-to-call phone, mailto email,
          Book Water Test CTA. Nothing else, so nothing distracts from
          local conversion. */}
      <PartnerHeader data={data} businessName={businessName} heroCta={heroCta} />
      <SchemaJsonLd data={breadcrumbSchema(breadcrumbs)} />
      <SchemaJsonLd data={localBusinessSchemaFor(data, canonicalSlug)} />

      {/* ── Hero (SCWT-STOREFRONT-BRAND-HERO · Feb 2026) ──────────
          Light AWARE brand hero — target palette: navy headline
          (#0d2451), teal accent (#106c81) for subheadline, buttons
          and icons. Right column is a clean lifestyle photo without
          an overlay card, per Feb 8 iteration. */}
      <section className="relative bg-white" data-testid="storefront-hero">
        <div className="max-w-6xl mx-auto px-6 py-8 md:py-12 grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          {/* ── LEFT: Copy + CTAs + trust strip ────────────────── */}
          <div>
            <h1
              data-testid="storefront-h1"
              className="text-4xl sm:text-5xl lg:text-[3.25rem] font-black leading-[1.05] whitespace-pre-line tracking-tight text-[#0d2451]"
            >
              {data.hero_headline
                || `Professional Water Testing\nfor ${region || "your area"}`}
            </h1>
            <h2 className="mt-2 text-xl sm:text-2xl lg:text-[1.75rem] font-black leading-[1.15] tracking-tight text-[#106c81]">
              {data.hero_subheadline
                || `By ${businessName} — your local AWARE Water Testing partner`}
            </h2>
            <p className="mt-4 text-base text-[#0d2451]/80 max-w-xl leading-relaxed">
              {data.hero_intro_override || (
                <>
                  <b>{businessName}</b> delivers lab-verified water testing
                  across {region || "the region"} as your local
                  <b> AWARE Water Testing</b> partner. From sample collection
                  to plain-English recommendations, you deal with a local
                  specialist who lives and works in your community —
                  professional laboratory results, delivered with
                  <b> on-the-ground</b> local support.
                </>
              )}
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link to={heroCta}>
                <Button
                  size="lg"
                  className="rounded-md bg-[#106c81] hover:bg-[#0a5a6b] text-white h-11 px-6 font-semibold shadow-sm"
                  data-testid="storefront-hero-book-cta"
                >
                  <CalendarClock className="w-4 h-4 mr-2" /> Book Your Water Test
                </Button>
              </Link>
              {services.length > 0 && (
                <a href="#services">
                  <Button
                    variant="outline"
                    size="lg"
                    className="rounded-md border-[#106c81] text-[#106c81] hover:bg-[#106c81]/5 h-11 px-6 font-semibold"
                    data-testid="storefront-hero-services-cta"
                  >
                    <FlaskConical className="w-4 h-4 mr-2" /> View Testing Options
                  </Button>
                </a>
              )}
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 max-w-lg">
              {HERO_TRUST_STRIP.map(({ icon: Icon, title, sub }) => (
                <div key={title} className="flex items-start gap-2">
                  <Icon className="w-5 h-5 text-[#106c81] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0d2451] leading-tight">{title}</div>
                    <div className="text-xs text-slate-500 leading-tight">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Lifestyle photo + Specialists overlay card ─── */}
          <div className="relative pb-12 md:pb-16">
            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-slate-100">
              <img
                src={data.hero_bg_url || BRAND_LIFESTYLE_IMG}
                alt={data.hero_bg_url
                  ? `${businessName} in the field`
                  : "AWARE Water Testing — professional water testing"}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                data-testid="storefront-hero-lifestyle-img"
              />
            </div>
            {/* "Your Local Water Specialists" overlay card — slightly
                overlaps the bottom of the photo on md+ (mt via negative
                margin), stacks below the image on mobile. Circular
                headshot on the left, brand copy + partner contact in
                the middle, four trust bullets on the right. */}
            <div
              className="md:absolute md:-bottom-2 md:left-4 md:right-4 mt-3 md:mt-0
                         bg-[#0d2451] text-white rounded-xl shadow-2xl
                         p-4 md:px-5 md:py-4 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-3 md:gap-5 items-center"
              data-testid="storefront-specialists-card"
            >
              <div className="shrink-0 mx-auto md:mx-0">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden ring-2 ring-white/20 bg-slate-800">
                  {data.franchisee_photo_url ? (
                    <img
                      src={data.franchisee_photo_url}
                      alt={data.franchisee_name || businessName}
                      className="w-full h-full object-cover"
                      data-testid="storefront-specialists-headshot"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-cyan-300">
                      <Droplets className="w-8 h-8" />
                    </div>
                  )}
                </div>
              </div>
              <div className="min-w-0 text-center md:text-left">
                <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold">Your Local Water Specialist</div>
                <div className="font-bold text-lg md:text-xl mt-0.5" data-testid="storefront-specialists-name">
                  {data.franchisee_name || businessName}
                </div>
                <p className="mt-1 text-xs md:text-[13px] text-slate-300 leading-snug">
                  We live and work in your community and understand local
                  water conditions. We&apos;re here to help you get clear
                  answers and practical solutions.
                </p>
                {/* Partner contact — phone + email pulled from the
                    landing page's own admin-editable fields. Both hide
                    themselves when blank so the card degrades cleanly. */}
                {(data.phone_display || data.email_display) && (
                  <div className="mt-2 flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-1 text-xs text-cyan-200">
                    {data.phone_display && (
                      <a
                        href={`tel:${data.phone_display.replace(/\s/g, "")}`}
                        className="inline-flex items-center gap-1 hover:text-white"
                        data-testid="storefront-specialists-phone"
                      >
                        <Phone className="w-3 h-3" /> {data.phone_display}
                      </a>
                    )}
                    {data.email_display && (
                      <a
                        href={`mailto:${data.email_display}`}
                        className="inline-flex items-center gap-1 hover:text-white truncate max-w-[220px]"
                        data-testid="storefront-specialists-email"
                      >
                        <Mail className="w-3 h-3" /> {data.email_display}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why choose this partner ─────────────────────────── */}
      <section className="border-b border-slate-100 bg-slate-50 py-10 md:py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Why choose {businessName}?</h2>
          {data.why_choose_bio ? (
            <p className="mt-4 text-base text-slate-700 max-w-3xl leading-relaxed whitespace-pre-wrap" data-testid="storefront-why-bio">
              {data.why_choose_bio}
            </p>
          ) : null}
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <WhyTile icon={MapPin}       title="Local PWS Partner" body="On-the-ground service in your community — not a call centre." />
            <WhyTile icon={FlaskConical} title="Independent laboratory" body="Every sample analysed at an Independent Testing Laboratory partner." />
            <WhyTile icon={Droplets}     title="Every water source" body="Rainwater tanks, private bores, town supply, dam and creek water." />
            <WhyTile icon={Wrench}       title="Corrective recommendations" body="If we find a problem, we help you fix it — filtration, UV, or BioCleanse." />
          </div>
        </div>
      </section>

      {/* ── Services ───────────────────────────────────────── */}
      {services.length > 0 && (
        <section id="services" className="border-b border-slate-100 py-10 md:py-12 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Services offered</h2>
            <p className="mt-2 text-slate-600 text-sm max-w-2xl">Only services this partner actually delivers are shown below. Every job starts with an independent water test to make sure the treatment matches the actual water chemistry.</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="storefront-services">
              {services.map((s) => {
                const Ico = s.icon;
                return (
                  <div key={s.key} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3" data-testid={`storefront-service-${s.key}`}>
                    <div className="w-10 h-10 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
                      <Ico className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">{s.label}</div>
                      <p className="mt-1 text-sm text-slate-600 leading-snug">{s.blurb}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Local service area ─────────────────────────────── */}
      {suburbs.length > 0 && (
        <section className="border-b border-slate-100 bg-slate-50 py-10 md:py-12">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Local service area</h2>
            <p className="mt-2 text-slate-600 text-sm">{businessName} covers {suburbs.length} suburb{suburbs.length === 1 ? "" : "s"} across {region || "the region"}.</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="storefront-suburbs">
              {suburbs.slice(0, 40).map((sub) => (
                <div key={sub} className="rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm text-slate-800 font-medium">
                  {sub}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Google reviews (Jun 2026 — hidden unless the partner
             has a Google Place ID configured AND Google returns a
             rating; the section never renders an empty block) ──── */}
      <GoogleReviewsSection slug={slug} />

      {/* ── Reviews ────────────────────────────────────────── */}
      {hasReviews && (
        <section className="border-b border-slate-100 py-10 md:py-12" data-testid="storefront-reviews">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Customer reviews</h2>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-2xl bg-white border border-slate-200 p-5"
                  data-testid={`storefront-review-${t.id}`}
                >
                  <div className="flex items-center gap-0.5 mb-3" aria-label={`${t.rating} out of 5`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < (t.rating || 5) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                    ))}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-4 flex items-center gap-3">
                    {t.photo_url && (
                      <img src={t.photo_url} alt={t.author} className="w-10 h-10 rounded-full object-cover" />
                    )}
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{t.author}</div>
                      {t.suburb && <div className="text-xs text-slate-500">{t.suburb}</div>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Before & After (optional) ──────────────────────── */}
      {hasGallery && (
        <section className="border-b border-slate-100 bg-slate-50 py-10 md:py-12" data-testid="storefront-gallery">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Before &amp; after</h2>
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="rounded-2xl overflow-hidden bg-white border border-slate-200" data-testid={`storefront-gallery-${g.id}`}>
                  <div className="grid grid-cols-2">
                    {g.before_url && <img src={g.before_url} alt={g.caption ? `Before — ${g.caption}` : "Before"} className="w-full aspect-square object-cover" />}
                    {g.after_url  && <img src={g.after_url}  alt={g.caption ? `After — ${g.caption}` : "After"}  className="w-full aspect-square object-cover" />}
                  </div>
                  {g.caption && <div className="px-4 py-3 text-xs text-slate-600">{g.caption}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ────────────────────────────────────────────── */}
      {hasFaqs && (
        <section className="border-b border-slate-100 py-10 md:py-12" data-testid="storefront-faqs">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Frequently asked questions</h2>
            <div className="mt-6 divide-y divide-slate-200 border-t border-slate-200">
              {faqs.map((f) => (
                <FaqItem key={f.id} q={f.question} a={f.answer} testId={`storefront-faq-${f.id}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Final CTA ──────────────────────────────────────── */}
      <section className="bg-slate-900 text-white py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Book your water test with {businessName}</h2>
          <p className="mt-3 text-slate-300 max-w-xl mx-auto">One booking. Full lab report. Follow-up by your local PWS Partner.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={heroCta}>
              <Button size="lg" className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 h-12 px-8 font-bold" data-testid="storefront-final-book-cta">
                Book Water Test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            {data.email_display && (
              <a href={`mailto:${data.email_display}`} className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white h-12 px-6 font-semibold hover:bg-white/10" data-testid="storefront-final-email">
                <Mail className="w-4 h-4" /> {data.email_display}
              </a>
            )}
          </div>
        </div>
      </section>

      <Footer partnerSlug={partnerSlug} />
    </div>
  );
}

// Fixed key order so the services grid renders in a consistent
// sequence regardless of the JSON key order returned by the API.
const SERVICE_KEYS_ORDERED = [
  "water_testing", "tank_cleaning", "filtration",
  "biocleanse", "uv_systems", "annual_servicing",
];

function WhyTile({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-bold text-slate-900">{title}</div>
        <p className="mt-1 text-sm text-slate-600 leading-snug">{body}</p>
      </div>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-4" data-testid={testId}>
      <button type="button" className="w-full flex items-start justify-between gap-3 text-left" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-5 h-5 mt-0.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{a}</div>}
    </div>
  );
}

// LocalBusiness structured data — every published storefront gets a
// per-partner JSON-LD block so Google can populate the knowledge panel
// with the right franchisee, address hint (region_label) and services.
function localBusinessSchemaFor(data, slug) {
  const url = `${BASE_URL}/pws-partner/${slug}`;
  const services = SERVICE_KEYS_ORDERED
    .filter((k) => data.services_offered?.[k])
    .map((k) => SERVICE_META[k].label);
  return {
    "@context": "https://schema.org",
    "@type":    "LocalBusiness",
    name:       data.business_name,
    image:      data.franchisee_photo_url || undefined,
    logo:       data.franchisee_logo_url  || undefined,
    url,
    telephone:  data.phone_display  || undefined,
    email:      data.email_display  || undefined,
    areaServed: (data.service_suburbs || []).slice(0, 30),
    description: `${data.business_name} — your local PWS Partner${data.region_label ? ` for ${data.region_label}` : ""}. Independent water testing plus tank cleaning, filtration and BioCleanse services.`,
    makesOffer: services.map((s) => ({ "@type": "Offer", name: s })),
  };
}
